import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { sanityClient, urlFor } from "@/lib/sanityClient";
import logoH from "@/assets/logo_h_3d.png";
import { CalendarBlank } from "@phosphor-icons/react";

const POSTS_QUERY = `*[_type == "post"] | order(publishedAt desc) {
  _id,
  title,
  "slug": slug.current,
  publishedAt,
  mainImage,
  excerpt,
  "author": author->{ name, image }
}`;

function SkeletonCard() {
  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 overflow-hidden animate-pulse">
      <div className="h-48 bg-slate-200 dark:bg-slate-700" />
      <div className="p-5 space-y-3">
        <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-3/4" />
        <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-full" />
        <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-5/6" />
        <div className="h-3 bg-slate-100 dark:bg-slate-600 rounded w-1/3 mt-4" />
      </div>
    </div>
  );
}

function PostCard({ post }) {
  const dateStr = post.publishedAt
    ? new Date(post.publishedAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
    : null;

  return (
    <Link
      to={`/blog/${post.slug}`}
      className="group bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 overflow-hidden shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 flex flex-col"
    >
      {post.mainImage ? (
        <img
          src={urlFor(post.mainImage).width(600).height(340).fit("crop").url()}
          alt={post.title}
          className="h-48 w-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
          loading="lazy"
        />
      ) : (
        <div className="h-48 bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-950/30 dark:to-slate-800 flex items-center justify-center">
          <span className="text-4xl">🏠</span>
        </div>
      )}
      <div className="p-5 flex flex-col flex-1">
        <h2 className="font-manrope text-lg font-bold text-slate-900 dark:text-white leading-snug group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors line-clamp-2">
          {post.title}
        </h2>
        {post.excerpt && (
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-2 line-clamp-3 flex-1">
            {post.excerpt}
          </p>
        )}
        <div className="flex items-center gap-2 mt-4 pt-4 border-t border-slate-100 dark:border-slate-700">
          {post.author?.image && (
            <img
              src={urlFor(post.author.image).width(32).height(32).fit("crop").url()}
              alt={post.author.name}
              className="w-6 h-6 rounded-full object-cover"
            />
          )}
          {post.author?.name && (
            <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">{post.author.name}</span>
          )}
          {dateStr && (
            <span className="text-xs text-slate-400 dark:text-slate-500 flex items-center gap-1 ml-auto">
              <CalendarBlank size={11} />
              {dateStr}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}

export default function Blog() {
  const { data: posts, isLoading, isError } = useQuery({
    queryKey: ["sanity-posts"],
    queryFn: () => sanityClient.fetch(POSTS_QUERY),
    staleTime: 5 * 60 * 1000,
  });

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 font-outfit">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/90 dark:bg-slate-900/90 backdrop-blur-sm border-b border-slate-100 dark:border-slate-800">
        <div className="max-w-5xl mx-auto px-4 md:px-8 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-1 select-none">
            <img src={logoH} alt="HostFolio" className="w-8 h-8 object-contain" />
            <span className="font-manrope text-lg font-bold">
              <span className="text-slate-800 dark:text-white">Host</span>
              <span className="text-amber-500">Folio</span>
            </span>
          </Link>
          <Link
            to="/?ref=blog"
            className="text-sm font-semibold px-4 py-1.5 rounded-xl bg-amber-400 hover:bg-amber-500 text-white transition"
          >
            Try Free →
          </Link>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 md:px-8 py-12">
        {/* Hero */}
        <div className="mb-10">
          <h1 className="font-manrope text-3xl md:text-4xl font-bold text-slate-900 dark:text-white tracking-tight">
            HostFolio Blog
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-2 text-lg">
            Guides, tips, and insights for Airbnb hosts in India.
          </p>
        </div>

        {/* Grid */}
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map(i => <SkeletonCard key={i} />)}
          </div>
        ) : isError ? (
          <div className="text-center py-20 text-slate-400">
            <p className="text-lg">Couldn't load posts right now. Check back soon.</p>
          </div>
        ) : !posts?.length ? (
          <div className="text-center py-20 text-slate-400">
            <p className="text-5xl mb-4">✍️</p>
            <p className="text-lg font-medium text-slate-600 dark:text-slate-300">No posts yet</p>
            <p className="text-sm mt-1">We're writing — come back soon.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map(post => <PostCard key={post._id} post={post} />)}
          </div>
        )}
      </div>

      {/* Footer CTA */}
      <div className="border-t border-slate-100 dark:border-slate-800 mt-16 py-10 text-center">
        <p className="text-slate-500 dark:text-slate-400 text-sm mb-3">
          Manage all your Airbnb properties from one dashboard
        </p>
        <Link
          to="/?ref=blog-footer"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-amber-400 hover:bg-amber-500 text-white font-semibold text-sm rounded-xl transition"
        >
          Start free with HostFolio →
        </Link>
      </div>
    </div>
  );
}
